import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BroadcastBanner — site-wide announcement bar for active 👨‍🍳 partner-egg
 * drops (Chief Cook David Lauer, etc.). Polls /api/partner-eggs/active and
 * shows the most-recent active banner. Dismissal is per-banner per-browser.
 */
export default function BroadcastBanner() {
  const [active, setActive] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // If a HolidayEggBanner is also active above us, it already provides the
  // top-of-page offset — skip ours to avoid double-pushing the page down.
  const [needsOffset, setNeedsOffset] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: pe }, { data: hd }] = await Promise.all([
          axios.get(`${API}/partner-eggs/active`),
          axios.get(`${API}/holiday/active`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const list = pe?.active || [];
        list.sort((a, b) => (b.announced_at || "").localeCompare(a.announced_at || ""));
        const top = list[0] || null;
        setActive(top);
        setNeedsOffset(!hd?.active);
        if (top) {
          const seenKey = `omni:partner-egg-dismissed:${top.key}:${top.banner_until}`;
          if (localStorage.getItem(seenKey)) setDismissed(true);
        }
      } catch {
        if (!cancelled) setActive(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!active || dismissed) return null;

  const t = active.theme || {};
  const bgStyle = {
    background: `linear-gradient(90deg, ${t.shadow || "#0A0A0A"} 0%, ${t.base || "#1E40AF"} 55%, ${t.shadow || "#0A0A0A"} 100%)`,
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(
        `omni:partner-egg-dismissed:${active.key}:${active.banner_until}`,
        "1"
      );
    } catch {
      /* no-op */
    }
  };

  return (
    <div
      data-testid="broadcast-banner"
      className={
        "relative w-full overflow-hidden border-y-2 z-[55] " +
        (needsOffset ? "mt-[100px] md:mt-[108px]" : "")
      }
      style={{ borderColor: t.ring || "#FCD34D", ...bgStyle }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4 flex-wrap">
        <div className="text-3xl">{active.icon || "👨‍🍳"}</div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.3em] font-bold"
            style={{ color: t.highlight || "#FCD34D" }}
          >
            New Partner Drop · {active.tier_name || "Signature"} · {active.letter_grade || "A★"}
          </div>
          <div className="font-display text-lg md:text-2xl text-white leading-tight">
            {active.label} egg just dropped 🥚
          </div>
          {active.blurb && (
            <div className="text-xs md:text-sm text-white/90 mt-1 line-clamp-2">
              {active.blurb}
            </div>
          )}
          <div
            className="text-[10px] uppercase tracking-widest mt-1"
            style={{ color: (t.highlight || "#FCD34D") + "CC" }}
          >
            Auto-claimed · banner active until {new Date(active.banner_until).toLocaleString()}
          </div>
        </div>
        <Link
          to={active.key === "david_lauer" ? "/chief-cook/david-lauer" : "/nest"}
          data-testid="broadcast-banner-cta"
          className="font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors"
          style={{
            background: t.highlight || "#FCD34D",
            color: "#0A0A0A",
            border: `1px solid ${t.ring || "#FCD34D"}`,
          }}
        >
          {active.key === "david_lauer" ? "Meet the Chef →" : "View My Nest →"}
        </Link>
        <button
          data-testid="broadcast-banner-dismiss"
          onClick={handleDismiss}
          className="text-white/60 hover:text-white text-sm leading-none"
          title="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
